import { supabase } from "./supabase";

type JsonRecord = Record<string, unknown>;
type Rpc = (
  name: string,
  args: { input_payload?: JsonRecord; payload?: JsonRecord },
) => PromiseLike<{
  data: unknown;
  error: { message: string } | null;
}>;

function idempotencyKey() {
  return globalThis.crypto.randomUUID();
}

export async function runSellerCommand(
  name: string,
  payload: JsonRecord,
  rpc: Rpc = (fn, args) => supabase.rpc(fn, args as never),
) {
  const { data, error } = await rpc(name, {
    input_payload: { ...payload, idempotencyKey: idempotencyKey() },
  });
  if (error) throw new Error(error.message || "Não foi possível concluir a ação do vendedor.");
  return data;
}

export async function runContractCommand(
  name: string,
  payload: JsonRecord,
  rpc: Rpc = (fn, args) => supabase.rpc(fn, args as never),
) {
  const { data, error } = await rpc(name, {
    payload: { ...payload, idempotencyKey: idempotencyKey() },
  });
  if (error) throw new Error(error.message || "Não foi possível atualizar a revisão interna.");
  return data;
}

export async function loadEligibleContractDocumentSnapshot(
  organizationId: string,
  contractId: string,
): Promise<JsonRecord> {
  const { data, error } = await supabase
    .from("contracts")
    .select(
      "contract_type, template_snapshot, product_snapshot, pricing_snapshot, coverage_snapshot",
    )
    .eq("organization_id", organizationId)
    .eq("id", contractId)
    .in("status", ["DRAFT", "AWAITING_DATA", "AWAITING_SIGNATURE"])
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data)
    throw new Error("Contrato não encontrado ou fora do estado elegível para revisão interna.");
  const row = data;
  return buildInternalDocumentSnapshot({
    contractType: row.contract_type,
    template: row.template_snapshot,
    product: row.product_snapshot,
    pricing: row.pricing_snapshot,
    coverage: row.coverage_snapshot,
  });
}

export function buildInternalDocumentSnapshot(input: JsonRecord): JsonRecord {
  return {
    contractType: input.contractType,
    template: input.template,
    product: input.product,
    pricing: input.pricing,
    coverage: input.coverage,
  };
}

export async function loadSellerSetup(organizationId: string, callerMembershipId: string | null) {
  const [employeesResult, sellersResult, teamsResult, membershipsResult] = await Promise.all([
    supabase
      .from("employees")
      .select("id, employee_number, membership_id, status, people(full_name)")
      .eq("organization_id", organizationId)
      .order("employee_number"),
    supabase
      .from("sellers")
      .select("id, employee_id, team_id")
      .eq("organization_id", organizationId),
    supabase
      .from("seller_teams")
      .select("id, name, supervisor_employee_id")
      .eq("organization_id", organizationId)
      .eq("status", "ACTIVE")
      .order("name"),
    supabase
      .from("organization_memberships")
      .select("id, profiles!inner(full_name, status)")
      .eq("organization_id", organizationId)
      .eq("status", "ACTIVE"),
  ]);
  const failed = [
    employeesResult.error,
    sellersResult.error,
    teamsResult.error,
    membershipsResult.error,
  ].find(Boolean);
  if (failed) throw new Error(failed.message);
  type EmployeeRow = {
    id: string;
    employee_number: string | null;
    membership_id: string | null;
    status: string;
    people: { full_name: string } | { full_name: string }[] | null;
  };
  type SellerRow = { id: string; employee_id: string; team_id: string | null };
  type TeamRow = { id: string; name: string; supervisor_employee_id: string | null };
  const employeeRows = (employeesResult.data as unknown as EmployeeRow[] | null) ?? [];
  const sellerRows = (sellersResult.data ?? []) as SellerRow[];
  const teamRows = (teamsResult.data ?? []) as TeamRow[];
  type MembershipRow = {
    id: string;
    profiles:
      { full_name: string; status: string } | { full_name: string; status: string }[] | null;
  };
  const membershipRows = (membershipsResult.data as unknown as MembershipRow[] | null) ?? [];
  let canManageEmployees = false;
  if (callerMembershipId) {
    const rolesResult = await supabase
      .from("membership_roles")
      .select("role_id")
      .eq("membership_id", callerMembershipId);
    if (rolesResult.error) throw new Error(rolesResult.error.message);
    const roleIds = ((rolesResult.data ?? []) as unknown as Array<{ role_id: string }>).map(
      (row) => row.role_id,
    );
    if (roleIds.length) {
      const rolePermissionsResult = await supabase
        .from("role_permissions")
        .select("permission_id")
        .in("role_id", roleIds);
      if (rolePermissionsResult.error) throw new Error(rolePermissionsResult.error.message);
      const permissionIds = (
        (rolePermissionsResult.data ?? []) as unknown as Array<{ permission_id: string }>
      ).map((row) => row.permission_id);
      if (permissionIds.length) {
        const adminPermissionResult = await supabase
          .from("permissions")
          .select("id")
          .eq("code", "admin.full")
          .in("id", permissionIds)
          .maybeSingle();
        if (adminPermissionResult.error) throw new Error(adminPermissionResult.error.message);
        canManageEmployees = Boolean(adminPermissionResult.data);
      }
    }
  }
  const sellerEmployeeIds = new Set(sellerRows.map((row) => row.employee_id));
  const existingEmployeeMembershipIds = new Set(
    employeeRows.map((row) => row.membership_id).filter((id): id is string => Boolean(id)),
  );
  const activeMemberships = membershipRows.flatMap((row) => {
    const profileValue = row.profiles;
    const profile = Array.isArray(profileValue) ? profileValue[0] : profileValue;
    if (!profile || profile.status !== "ACTIVE") return [];
    return [{ id: row.id, name: profile.full_name }];
  });
  const employees = employeeRows
    .filter(
      (row) =>
        row.status === "ACTIVE" &&
        row.membership_id &&
        activeMemberships.some((membership) => membership.id === row.membership_id),
    )
    .map((row) => {
      const personValue = row.people;
      const person = Array.isArray(personValue) ? personValue[0] : personValue;
      return {
        id: row.id,
        employeeNumber: row.employee_number ?? "",
        name: (person as { full_name?: string } | null)?.full_name ?? "Colaborador",
      };
    });
  return {
    employees,
    eligibleEmployees: employees.filter((employee) => !sellerEmployeeIds.has(employee.id)),
    canManageEmployees,
    onboardingMemberships: buildOnboardableMemberships(
      activeMemberships,
      existingEmployeeMembershipIds,
    ),
    sellerTeamIds: Object.fromEntries(sellerRows.map((row) => [row.id, row.team_id])),
    teams: teamRows.map((row) => ({
      id: row.id,
      name: row.name,
      supervisorEmployeeId: row.supervisor_employee_id,
    })),
  };
}

export function buildOnboardableMemberships(
  activeMemberships: Array<{ id: string; name: string }>,
  existingEmployeeMembershipIds: ReadonlySet<string>,
) {
  return activeMemberships.filter(
    (membership) => !existingEmployeeMembershipIds.has(membership.id),
  );
}

export function canOnboardMember(
  membershipId: string,
  loading: boolean,
  canManageEmployees: boolean,
  availableMembershipIds: string[],
) {
  return !loading && canManageEmployees && availableMembershipIds.includes(membershipId);
}

export function contractStageFromInternalStatus(
  status: string | null | undefined,
  baseStatus: string,
) {
  if (status === "IN_REVIEW") return "EM REVISÃO" as const;
  if (status === "INTERNALLY_APPROVED") return "APROVADO INTERNAMENTE" as const;
  if (status === "REJECTED") return "REJEITADO" as const;
  if (status === "DRAFT") return "RASCUNHO" as const;
  return baseStatus === "DRAFT" ||
    baseStatus === "AWAITING_DATA" ||
    baseStatus === "AWAITING_SIGNATURE"
    ? ("SEM REVISÃO INTERNA" as const)
    : ("FORA DO FLUXO INTERNO" as const);
}

export function canCreatePersistentSeller(input: {
  employeeId: string;
  referralCode: string;
  loading: boolean;
  eligibleEmployeeIds: string[];
}) {
  return (
    !input.loading &&
    input.eligibleEmployeeIds.includes(input.employeeId) &&
    input.referralCode.trim().length > 0
  );
}

export function canStartInternalContract(contractId: string | undefined, baseStatus: string) {
  return (
    Boolean(contractId) && ["DRAFT", "AWAITING_DATA", "AWAITING_SIGNATURE"].includes(baseStatus)
  );
}
